import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSimpleAuth } from '../../lib/supabase';
import { supabase } from '../../lib/supabase';
import {
  Camera,
  Save,
  Edit3,
  User,
  Mail,
  CheckCircle,
  AlertCircle,
  FileText,
  Folder
} from 'lucide-react';

export function AdminProfileSimplified() {
  const { user } = useSimpleAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState({
    displayName: '',
    email: user?.email || '',
    avatarUrl: '',
    bio: '',
    role: 'Entrepreneur'
  });

  const [articlesCount, setArticlesCount] = useState(0);
  const [projectsCount, setProjectsCount] = useState(0);

  useEffect(() => {
    const fetchAdminProfile = async () => {
      if (user) {
        // First, try to get the latest image from the bucket
        const { data: files, error: bucketError } = await supabase.storage
          .from('profile-images')
          .list(user.id, {
            limit: 100,
            sortBy: { column: 'created_at', order: 'desc' }
          });
          
        let bucketAvatarUrl = '';
        if (!bucketError && files && files.length > 0) {
          const validExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
          const latestImage = files.find(file => {
            const ext = file.name.split('.').pop()?.toLowerCase();
            return ext && validExtensions.includes(ext);
          });
          
          if (latestImage) {
            const filePath = `${user.id}/${latestImage.name}`;
            const { data: { publicUrl } } = supabase.storage
              .from('profile-images')
              .getPublicUrl(filePath);
            bucketAvatarUrl = publicUrl;
          }
        }
        
        // Then fetch admin profile from database
        const { data, error } = await supabase
          .from('admin_profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          // Error fetching profile
        } else if (data) {
          // Prioritize bucket > database > auth metadata
          const avatarUrl = bucketAvatarUrl || data.avatar_url || user.user_metadata.avatar_url || '';
          
          setProfile({
            displayName: data.display_name || user.user_metadata.display_name || 'Admin User',
            email: user.email || '',
            avatarUrl: avatarUrl,
            bio: data.bio || '',
            role: data.role || 'Entrepreneur'
          });
          
          // If bucket has newer image, sync it to database
          if (bucketAvatarUrl && bucketAvatarUrl !== data.avatar_url) {
            await supabase
              .from('admin_profiles')
              .update({ avatar_url: bucketAvatarUrl })
              .eq('id', user.id);
          }
        } else {
          // No admin profile exists yet, use bucket or user metadata
          const avatarUrl = bucketAvatarUrl || user.user_metadata.avatar_url || '';
          
          setProfile({
            displayName: user.user_metadata.display_name || 'Admin User',
            email: user.email || '',
            avatarUrl: avatarUrl,
            bio: '',
            role: 'Entrepreneur'
          });
        }
      }
    };
    fetchAdminProfile();
  }, [user]);

  // Fetch real counts
  useEffect(() => {
    const fetchCounts = async () => {
      if (user) {
        // Fetch articles count
        const { count: articlesCount } = await supabase
          .from('articles')
          .select('*', { count: 'exact', head: true });
        
        setArticlesCount(articlesCount || 0);
        
        // Fetch projects count
        const { count: projectsCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true });
        
        setProjectsCount(projectsCount || 0);
      }
    };
    fetchCounts();
  }, [user]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    try {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      // Generate a unique filename using user ID, timestamp and original extension
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (!fileExt) {
        throw new Error('File must have a valid extension');
      }
      
      // Validate file extension
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
      if (!allowedExtensions.includes(fileExt)) {
        throw new Error(`Only JPG, PNG, WebP, and GIF images are allowed. Got: ${fileExt}`);
      }
      
      const timestamp = Date.now();
      const fileName = `${user?.id}/${timestamp}.${fileExt}`;
      let filePath = fileName;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true  // Allow overwriting existing files
        });

      if (uploadError) {
        // Handle specific storage audit log error
        if (uploadError.message?.includes('objects_audit_log') || 
            uploadError.message?.includes('permission denied for schema storage')) {
          // Try upload without upsert as a fallback
          const retryFilePath = `${user?.id}/${Date.now()}-retry.${fileExt}`;
          const { error: retryError } = await supabase.storage
            .from('profile-images')
            .upload(retryFilePath, file, {
              cacheControl: '3600',
              upsert: false
            });
          
          if (retryError) {
            throw new Error('Storage configuration issue. Please contact support or try again later.');
          }
          
          // Update file path for the retry
          filePath = retryFilePath;
        } else {
          throw uploadError;
        }
      }

      // Verify the file was actually uploaded successfully
      const { data: uploadedFile, error: checkError } = await supabase.storage
        .from('profile-images')
        .list(user?.id || '', { 
          limit: 100,
          search: filePath.split('/').pop() 
        });
        
      if (checkError || !uploadedFile || uploadedFile.length === 0) {
        throw new Error('File upload verification failed - file may not have been saved');
      }

      // Get the public URL for the uploaded file
      const { data: publicUrlData } = supabase.storage
        .from('profile-images')
        .getPublicUrl(filePath);
      const publicUrl = publicUrlData.publicUrl;
      
      // Test if the URL is actually accessible
      try {
        const response = await fetch(publicUrl, { method: 'HEAD' });
        
        if (!response.ok) {
          throw new Error(`Image URL is not accessible: ${response.status} ${response.statusText}`);
        }
      } catch (fetchError) {
        throw new Error('Uploaded image is not accessible via public URL');
      }

      // Update the profile state with the public URL
      setProfile(prev => ({ ...prev, avatarUrl: publicUrl }));
      
      // Also update the auth user metadata immediately
      const { error: authUpdateError } = await supabase.auth.updateUser({
        data: { avatar_url: publicUrl }
      });
      
      if (authUpdateError) {
        // Handle error silently
      }
      
      setSuccessMessage('Profile image uploaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Trigger profile refresh in header
      window.dispatchEvent(new CustomEvent('profile-updated'));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to upload image');
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Update both auth metadata and admin_profiles table
      const updates = [
        // Update auth user metadata
        supabase.auth.updateUser({
          data: {
            display_name: profile.displayName,
            avatar_url: profile.avatarUrl
          }
        }),
        // Update admin_profiles table (upsert to create if doesn't exist)
        supabase
          .from('admin_profiles')
          .upsert({
            id: user?.id || '',
            display_name: profile.displayName,
            avatar_url: profile.avatarUrl,
            bio: profile.bio,
            role: profile.role
          })
      ];

      const results = await Promise.all(updates);
      
      // Check for errors
      for (const result of results) {
        if (result.error) {
          throw result.error;
        }
      }

      setIsEditing(false);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Trigger profile refresh in header
      window.dispatchEvent(new CustomEvent('profile-updated'));
      
      // Force refresh the session to update all components
      const { data: { session } } = await supabase.auth.refreshSession();
      if (session) {
        // Small delay to ensure state propagates
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      setError('Failed to update profile');
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 dark:from-purple-500/20 dark:via-pink-500/20 dark:to-orange-500/20 rounded-3xl p-8 backdrop-blur-sm border border-purple-200/50 dark:border-purple-700/50"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent mb-2">
              My Profile
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Manage your personal information
            </p>
          </div>
          
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-6 py-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-purple-200 dark:border-purple-700 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-200 font-medium text-purple-600 dark:text-purple-400"
            >
              <Edit3 className="w-5 h-5" />
              Edit Profile
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setIsEditing(false)}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-200 font-medium disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Alerts */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 p-4 rounded-xl flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5" />
            {successMessage}
          </motion.div>
        )}
        
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-4 rounded-xl flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5" />
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Profile Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Avatar Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="md:col-span-1"
        >
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-6 text-center">
            <div className="relative inline-block mb-4">
              <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-white dark:border-gray-700 shadow-xl">
                {profile.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={async (e) => {
                      // Clear the broken avatar URL from state and database
                      setProfile(prev => ({ ...prev, avatarUrl: '' }));
                      
                      // Update database to remove broken URL
                      if (user?.id) {
                        await supabase
                          .from('admin_profiles')
                          .update({ avatar_url: null })
                          .eq('id', user.id);
                          
                        await supabase.auth.updateUser({
                          data: { avatar_url: null }
                        });
                      }
                      
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.parentElement?.querySelector('.fallback-avatar') as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`fallback-avatar absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center ${!profile.avatarUrl ? 'flex' : 'hidden'}`}
                >
                  <User className="w-16 h-16 text-white" />
                </div>
              </div>
              {isEditing && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-colors shadow-lg"
                  >
                    <Camera className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {profile.displayName}
            </h2>
            
            {/* Role - Make it editable */}
            {isEditing ? (
              <input
                type="text"
                value={profile.role}
                onChange={(e) => setProfile(prev => ({ ...prev, role: e.target.value }))}
                className="w-full text-center px-3 py-1 mt-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                placeholder="Your role"
              />
            ) : (
              <p className="text-gray-600 dark:text-gray-400">{profile.role}</p>
            )}
            
            {/* Content Stats */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <FileText className="w-4 h-4" />
                  Articles
                </span>
                <span className="font-bold text-gray-900 dark:text-white">{articlesCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <Folder className="w-4 h-4" />
                  Projects
                </span>
                <span className="font-bold text-gray-900 dark:text-white">{projectsCount}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Profile Details */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="md:col-span-2"
        >
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              Profile Information
            </h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profile.displayName}
                    onChange={(e) => setProfile(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">{profile.displayName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <p className="text-gray-900 dark:text-white flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {profile.email}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bio
                </label>
                {isEditing ? (
                  <textarea
                    value={profile.bio}
                    onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                    rows={4}
                    placeholder="Tell us about yourself..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-white">
                    {profile.bio || <span className="text-gray-500">No bio added yet</span>}
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}